import getSymbolFromCurrency from "currency-symbol-map";
import { isArray, isEqual, isObject, transform } from "lodash";

import {
  AudienceDto,
  ExperienceDto,
  ExperienceListDto,
  ExperiencePageTargetingDto,
  MetricType,
  OfferDto,
  PartialUserInterfaceDto,
  UserInterfaceDto,
} from "@intelligems/ig-types";
import {
  getEndedAtTs,
  getPausedAtTs,
  getStartedAtTs,
} from "@intelligems/ig-utils";

import {
  noDecimalCurrencyFormatter,
  noDecimalNumberFormatter,
} from "../../api/formatters/dashboard";
import {
  CampaignTableSummaryType,
  FoundExperimentDetails,
} from "../../types/experience";
import { ExperienceFe } from "../../types/schemas/experiences";
import { VariationFe } from "../../types/schemas/variations";
import { shortDateTimeConverter } from "../../utils/dateConverters";

export const getPersonalizationDuration = (
  experience: ExperienceFe,
  storeCurrencyLocale: string
) => {
  const pausedAtTs = getPausedAtTs(experience.experienceActions)?.actionTs;
  const endedAtTs = getEndedAtTs(experience.experienceActions)?.actionTs;
  const latestStartedAt = getStartedAtTs(
    experience.experienceActions
  )?.actionTs;
  if (experience.status === "pending") {
    return "";
  }
  if (experience.status === "started") {
    return `Active since ${shortDateTimeConverter(
      latestStartedAt!,
      storeCurrencyLocale
    )}`;
  }
  return `${shortDateTimeConverter(
    latestStartedAt!,
    storeCurrencyLocale
  )} - ${shortDateTimeConverter(pausedAtTs ?? endedAtTs, storeCurrencyLocale)}`;
};

export const getPersonalizationTotalsFormattedData = (
  data: MetricType,
  storeCurrencyConfigValue: { code: string; locale: string }
) => {
  return {
    visitors: noDecimalNumberFormatter(
      (data && (data["VISITORS"] as number)) || 0,
      false,
      storeCurrencyConfigValue
    ),
    orders: noDecimalNumberFormatter(
      (data && (data["ORDERS"] as number)) || 0,
      false,
      storeCurrencyConfigValue
    ),
    revenue: noDecimalCurrencyFormatter(
      (data && (data["TOTAL_REVENUE"] as number)) || 0,
      false,
      storeCurrencyConfigValue
    ),
  };
};

export const isUsedInExperiment = (
  experienceList: ExperienceListDto[],
  campaignId: string | null
): FoundExperimentDetails => {
  let experimentFound: FoundExperimentDetails = {
    name: undefined,
    id: undefined,
  };

  for (const experiment of experienceList) {
    for (const group of experiment.variations) {
      if (group.testExperienceId === campaignId && !experiment.endedAtTs) {
        experimentFound = { name: experiment.name, id: experiment.id };
        break;
      }
    }
  }

  return experimentFound;
};

export const difference = (origObj: any, newObj: any): any => {
  function changes(newObj: any, origObj: any) {
    let arrayIndexCounter = 0;
    return transform(newObj, function (result, value, key) {
      if (!isEqual(value, origObj[key])) {
        const resultKey = isArray(origObj) ? arrayIndexCounter++ : key;
        // @ts-ignore
        result[resultKey] =
          isObject(value) && isObject(origObj[key])
            ? changes(value, origObj[key])
            : value;
      }
    });
  }

  return changes(newObj, origObj);
};

export const generateSummary = (
  campaignState: ExperienceFe | ExperienceDto,
  discountState: OfferDto,
  storeCurrencyCode: string | undefined,
  campaignInterfaces?: UserInterfaceDto[]
): CampaignTableSummaryType[] => {
  const sumArray: CampaignTableSummaryType[] = [];
  const isFreeGift = discountState.discountApplicationType === "freeGift";
  const userInterfacesArr: string[] = [];
  if (campaignInterfaces) {
    campaignInterfaces.forEach((userInterface) => {
      if (userInterface.isEnabled) {
        userInterfacesArr.push(
          `${
            userInterface.widget?.widgetType === "quantityButtons"
              ? "Quantity Buttons Enabled"
              : userInterface.widget?.widgetType === "discountProgressBar"
                ? "Progress Bar Enabled"
                : "Message Box Enabled"
          }`
        );
      }
    });
  }

  if (discountState.discountApplicationType === "freeShipping") {
    sumArray.push({
      value: "Offer: Free Shipping",
      subValues: [...userInterfacesArr],
    });
  } else if (isFreeGift) {
    sumArray.push({
      value: "Offer: Free Gift",
      subValues: [...userInterfacesArr],
    });
  } else if (discountState.discountApplicationType === "tieredDiscount") {
    sumArray.push({
      value: "Offer: Volume Discount",
      subValues: [...userInterfacesArr],
    });
  } else if (discountState.discountApplicationType === "cartDiscount") {
    sumArray.push({
      value: "Offer: Promotion",
      subValues: [...userInterfacesArr],
    });
  }
  if (discountState.discountType && discountState.unitType) {
    const offerIndex = sumArray.findIndex((sum) => sum.value.includes("Offer"));
    discountState.tiers.forEach((tier) => {
      sumArray[offerIndex].subValues.push(
        `${
          discountState.discountType === "dollar"
            ? getSymbolFromCurrency(storeCurrencyCode || "USD")
            : ""
        }${isFreeGift ? "Gift with purchase" : tier.unitDiscount}${
          discountState.discountType === "percentage"
            ? isFreeGift
              ? ""
              : "%"
            : ""
        }${!isFreeGift ? " off at " : " at "}${
          discountState.unitType === "dollar"
            ? getSymbolFromCurrency(storeCurrencyCode || "USD")
            : ""
        }${tier.minimumUnits} ${
          discountState.unitType === "unit"
            ? `unit${tier.minimumUnits === 1 ? "" : "s"}`
            : ""
        }`
      );
    });
  }

  if (campaignState.testTypes.hasTestContentUrl) {
    sumArray.push({ value: "URL Redirect", subValues: [] });
  }
  if (campaignState.testTypes.hasTestContentTemplate) {
    sumArray.push({ value: "Template Redirect", subValues: [] });
  }
  if (campaignState.testTypes.hasTestContentTheme) {
    sumArray.push({ value: "Theme Redirect", subValues: [] });
  }
  if (campaignState.testTypes.hasTestContentOnsite) {
    sumArray.push({ value: "Content Edits", subValues: [] });
  }

  if (campaignState.testTypes.hasTestOnsiteInjections) {
    sumArray.push({ value: "Styles & Javascript", subValues: [] });
  }
  if (campaignState.testTypes.hasTestPricing) {
    sumArray.push({ value: "Price Modification", subValues: [] });
  }

  return sumArray;
};

export const generateTargetingSummary = (
  audience: AudienceDto | undefined | null,
  groupAudience: AudienceDto | undefined | null,
  experiencePageTargetingState: ExperiencePageTargetingDto[] | undefined,
  experience: ExperienceFe | ExperienceDto
): CampaignTableSummaryType[] => {
  const sumArray: CampaignTableSummaryType[] = [];

  if (
    (groupAudience &&
      (groupAudience.excludeCurrency.exclude ||
        groupAudience.filters.length > 0)) ||
    experience.requiresLink
  ) {
    sumArray.push({ value: "Audience Targeting Ignored", subValues: [] });
  } else {
    sumArray.push({ value: "No Audience Targeting", subValues: [] });
  }

  if (experiencePageTargetingState && experiencePageTargetingState.length > 0) {
    sumArray.push({ value: "Page Targeting Enabled", subValues: [] });
  } else {
    sumArray.push({ value: "No Page Targeting", subValues: [] });
  }

  if (experience.requiresLink) {
    sumArray.push({ value: "Requires link", subValues: [] });
  } else {
    sumArray.push({ value: "No Link Required", subValues: [] });
  }
  return sumArray;
};

export const getRowData = (
  variations: VariationFe[],
  storeCurrencyCode: string | undefined,
  allUserInterfaces: UserInterfaceDto[],
  allExperiences: ExperienceDto[] | ExperienceListDto[],
  experienceWideAudience: AudienceDto
) => {
  const rows = ["Modifications", "Targeting"];

  return rows.map((row) => {
    const obj = { none: row };
    for (const variation of variations) {
      const foundExperience = allExperiences.find(
        (experience) => experience.id === variation?.testExperienceId
      );
      if (variation?.testExperienceId && foundExperience) {
        if (row === "Modifications") {
          if (foundExperience.variations[0]?.offer) {
            obj[foundExperience.id || ""] = generateSummary(
              foundExperience,
              foundExperience.variations[0].offer,
              storeCurrencyCode,
              allUserInterfaces.filter(
                (userInterface) =>
                  userInterface.variationId === foundExperience.variations[0].id
              )
            );
          } else obj[foundExperience.id || ""] = "";
          obj["rowHeight"] = 165;
        } else if (row === "Targeting") {
          obj[foundExperience.id || ""] = generateTargetingSummary(
            experienceWideAudience,
            foundExperience.audience,
            foundExperience.experiencePageTargeting,
            foundExperience
          );
          obj["rowHeight"] = 165;
        }
      }
    }
    return obj;
  });
};

export const toBuyMoreTemplateReplacements = (text: string) => {
  let result = text || "";

  result = result.replaceAll("@[#more](more)", "#more");
  result = result.replaceAll("@[#unitName](unitName)", "#unitName");
  result = result.replaceAll("@[#quantity](quantity)", "#quantity");
  result = result.replaceAll("@[#discount](discount)", "#discount");
  result = result.replaceAll("@[#rewardText](rewardText)", "#rewardText");
  return result;
};

export const fromBuyMoreTemplateReplacements = (text: string) => {
  let result = text || "";

  result = result.replaceAll(/\s+#more\s+/g, " @[#more](more) ");
  result = result.replaceAll(/\s+#unitName\s+/g, " @[#unitName](unitName) ");
  result = result.replaceAll("#quantity", "@[#quantity](quantity)");
  result = result.replaceAll(/\s+#discount\s+/g, " @[#discount](discount) ");
  result = result.replaceAll(
    /\s+#rewardText\s+/g,
    " @[#rewardText](rewardText) "
  );
  return result;
};

export const shouldFreeShippingBeChecked = (
  discountState: OfferDto,
  currTierIndex: number
) => {
  if (discountState.tiers[currTierIndex].isFreeShipping) {
    return { isFreeShipping: true, disabled: false };
  }
  const allTiersBeforeCurrTier = discountState.tiers.slice(0, currTierIndex);

  return {
    isFreeShipping: allTiersBeforeCurrTier.some((tier) => tier.isFreeShipping),
    disabled:
      allTiersBeforeCurrTier.some((tier) => tier.isFreeShipping) &&
      !discountState.tiers[currTierIndex].isFreeShipping,
  };
};

export const areOtherOffersDisabled = (
  key: string,
  modificationsInUse: string[],
  isHeadless: boolean,
  hasFeatureAccess: boolean,
  isPersonalizationActive: boolean,
  shouldDuplicateProducts: boolean,
  showInactiveSubWarning
) => {
  if (showInactiveSubWarning) {
    return {
      isDisabled: true,
      reason: "Approve billing to add modifications to this personalization.",
      showLock: true,
    };
  }
  if (isPersonalizationActive && !hasFeatureAccess) {
    return {
      isDisabled: true,
      reason: "Ineligible mod and active personalization",
      showLock: true,
    };
  }
  if (key === "price" && shouldDuplicateProducts) {
    return {
      isDisabled: true,
      reason: "Duplicate products are not supported for personalizations.",
      showLock: true,
    };
  }
  if (modificationsInUse.length === 0) {
    return { isDisabled: false, reason: "" };
  }

  if (modificationsInUse.includes(key)) {
    return {
      isDisabled: true,
      reason: "This modification is already in use.",
    };
  }
  if (key === "onsiteEdits" && isHeadless) {
    return {
      isDisabled: true,
      showLock: true,
      reason:
        "Content edits are unavailable for headless personalizations. Dynamically replace content on your site directly.",
    };
  }
  const modificationDisabledReasons = {
    freeGift: ["tieredDiscount", "cartDiscount", "freeShipping", "price"],
    tieredDiscount: ["freeGift", "cartDiscount", "freeShipping", "price"],
    cartDiscount: ["freeGift", "tieredDiscount", "freeShipping", "price"],
    freeShipping: ["freeGift", "tieredDiscount", "cartDiscount", "price"],
    template: ["theme"],
    theme: ["template"],
    price: ["tieredDiscount", "cartDiscount", "freeGift", "freeShipping"],
  };

  for (const modification in modificationDisabledReasons) {
    if (modificationsInUse.includes(modification)) {
      if (key === modification) {
        return {
          isDisabled: true,
          reason: "This modification is already in use.",
        };
      }
      if (modificationDisabledReasons[modification].includes(key)) {
        return {
          isDisabled: true,
          reason:
            key === "template"
              ? "A theme modification is already in use and cannot be combined with a template modification."
              : key === "theme"
                ? "A template modification is already in use and cannot be combined with a theme modification."
                : key === "price"
                  ? "A price modification cannot be combined with an offer modification."
                  : "Another offer modification is already in use.",
        };
      }
    }
  }

  return { isDisabled: false, reason: "" };
};

export const userInterfacesHasUnsavedChanges = (
  userInterface: PartialUserInterfaceDto | undefined,
  storedUserInterface: PartialUserInterfaceDto | undefined
) => {
  if (!userInterface && !storedUserInterface) {
    return false;
  }
  if (!userInterface && storedUserInterface) {
    return true;
  }
  if (!storedUserInterface && !userInterface?.isEnabled) {
    return false;
  }
  if (!storedUserInterface && userInterface?.isEnabled) {
    return true;
  } else {
    return (
      JSON.stringify(userInterface) !== JSON.stringify(storedUserInterface)
    );
  }
};
